/**
 * The per-look review surface: one of a member's looks for tomorrow, the
 * context it was built from, and what a stylist can do with it - approve it as
 * generated, edit it and say what changed and why, or reject it so a
 * replacement is generated and comes back for review.
 *
 * The avatar render happens after approval, so what is reviewed here is the
 * garment combination beside the member's avatar, never a rendered try-on.
 *
 * On a wide screen the page splits in two: the combination, the decision form
 * and the wardrobe scroll on the left, while the member - who they are, what
 * the member read knows about them, and the slot's facts - stays put on the
 * right, so the person is in view for the whole review.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft } from '@untitledui/icons';
import {
  dayAheadWardrobeApiV1StylistOotdDayAheadSlotIdWardrobeGet,
  type MemberDetail,
} from '@closet-theory/api';
import { useParams, useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, MetaItem, Section, StatusBadge } from '@ui/console/primitives';
import { FormCard, TextAreaField, TextField } from '@ui/console/forms';
import { ActionDialog } from '@ui/console/PageShell';
import { Page, ResourceState } from './components';
import { GarmentImage } from './GarmentImage';
import { relativeTime, shortDate } from './member-format';
import { ProfileGroup, profileGroups, profileUpdated } from './member-profile';
import { memberEmail, memberService } from './member-service';
import { Card, DetailList, GarmentThumbs, MemberAvatar } from './member-ui';
import { assignedLabel, parseScope, reviewHref, useReviewQueue } from './ReviewQueue';
import { istDay, weekdayFor } from './review-cutoff';
import { configureStylistApi, handleUnauthorized } from './auth';
import {
  diffCombination,
  reviewService,
  reviewerLabel,
  submissionBlockedReason,
  type DecisionReceipt,
  type QueuedOutfit,
  type RejectReceipt,
  type ReviewGarment,
  type ReviewService,
  type ReviewSubmission,
} from './review-service';

export type WardrobePage = {
  items: ReviewGarment[];
  total: number;
  limit: number;
  offset: number;
};

type WardrobeReader = (
  slotId: string,
  q: string,
  limit: number,
  offset: number,
) => Promise<WardrobePage | undefined>;

async function readSlotWardrobe(
  slotId: string,
  q: string,
  limit: number,
  offset: number,
): Promise<WardrobePage | undefined> {
  configureStylistApi();
  const result = await dayAheadWardrobeApiV1StylistOotdDayAheadSlotIdWardrobeGet({
    path: { slot_id: slotId },
    query: { q, limit, offset },
  });
  handleUnauthorized(result.response.status);
  if (result.error || !result.data) throw new Error('The wardrobe could not be loaded.');
  return {
    items: result.data.items.map((item) => ({
      garmentId: item.garment_id,
      title: item.title,
      category: item.category,
      tags: item.tags,
      imageUrl: item.image_url,
    })),
    total: result.data.total,
    limit: result.data.limit,
    offset: result.data.offset,
  };
}

const WARDROBE_PAGE_SIZE = 24;

/** The assignment-scoped member read; a member outside the assignment is refused, never leaked. */
type MemberReader = (memberId: string) => Promise<MemberDetail>;

const readMember: MemberReader = (memberId) => memberService.member(memberId);

/** The combination grid: wider now that the page uses the whole viewport. */
const GARMENT_GRID = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

/**
 * The stylist this decision covers for: the member's assigned stylist, when
 * that is someone other than the signed-in stylist. Undefined for your own
 * member, an unassigned one, or an API that does not report ownership.
 */
export function coveringFor(outfit: QueuedOutfit): string | undefined {
  const owner = outfit.ownership;
  if (!owner || owner.isMine || !owner.assignedStylistId) return undefined;
  return owner.assignedStylistName ?? 'another stylist';
}

/** The generator's reason for putting these garments together, when it gave one. */
function WhyThisCombination({ outfit }: { outfit: QueuedOutfit }) {
  if (!outfit.compatibilityReason) return null;
  return (
    <Card>
      <h3 className="text-sm font-semibold text-primary">Why this combination</h3>
      <p className="text-sm text-secondary">{outfit.compatibilityReason}</p>
    </Card>
  );
}

/** Who settled a reviewed slot, when, and what they said. Nothing for an unreviewed one. */
function ReviewRecord({ outfit }: { outfit: QueuedOutfit }) {
  const reviewer = reviewerLabel(outfit);
  if (!reviewer && !outfit.stylistComment) return null;
  return (
    <DetailList
      dense
      items={[
        ['Reviewed by', reviewer ?? 'Not recorded'],
        ['Reviewed', outfit.reviewedAt ? relativeTime(outfit.reviewedAt) : 'Not recorded'],
        ...(outfit.stylistComment
          ? [['Stylist comment', outfit.stylistComment] as [string, string]]
          : []),
      ]}
    />
  );
}

function GarmentDetails({ garment }: { garment: ReviewGarment }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
        {garment.category || 'Uncategorized'}
      </span>
      <span className="truncate text-sm font-semibold text-primary">
        {garment.title || 'Untitled garment'}
      </span>
      <span className="text-xs text-tertiary">
        {garment.tags.filter(Boolean).join(' · ') || 'No tags recorded'}
      </span>
    </div>
  );
}

function GarmentCard({ garment, actions }: { garment: ReviewGarment; actions: ReactNode }) {
  return (
    <li className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-secondary bg-primary">
      <GarmentImage garment={garment} size="card" />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <GarmentDetails garment={garment} />
        <div className="mt-auto flex flex-wrap items-center gap-2">{actions}</div>
      </div>
    </li>
  );
}

/**
 * The verdict the server derived and the diff it stored, never the local
 * presentational one. The garment ids are named where this outfit knows the
 * garment and left as ids where it does not.
 */
function ServerVerdict({
  receipt,
  nameOf,
}: {
  receipt: DecisionReceipt;
  nameOf: (garmentId: string) => string;
}) {
  const lines: Array<[string, string[]]> = [
    ['Added', receipt.garmentsAdded],
    ['Removed', receipt.garmentsRemoved],
  ];
  return (
    <div className="flex flex-col gap-1">
      <span>
        {receipt.decision === 'edited'
          ? 'The server recorded this as an edit.'
          : 'The server recorded this as an approval.'}
      </span>
      {lines.map(([label, ids]) =>
        ids.length ? <span key={label}>{`${label}: ${ids.map(nameOf).join(', ')}`}</span> : null,
      )}
      {receipt.decisionId ? (
        <span className="text-xs text-tertiary">Decision {receipt.decisionId}</span>
      ) : null}
    </div>
  );
}

/** The try-on picture's state in a stylist's words; it is only rendered after approval. */
function renderLabel(outfit: QueuedOutfit): string {
  switch (outfit.renderStatus) {
    case 'ready':
      return 'Ready';
    case 'rendering':
      return 'Rendering now';
    case 'failed':
      return outfit.renderError ? `Failed · ${outfit.renderError}` : 'Failed';
    default:
      return 'Rendered after approval';
  }
}

/**
 * Everything the reviewer can know about the person this outfit is for: the
 * queue's member details, the slot's facts, and the assignment-scoped member
 * read - all of it, laid out to stay in view beside the scrolling combination.
 */
function MemberBrief({
  outfit,
  memberReader,
}: {
  outfit: QueuedOutfit;
  memberReader: MemberReader;
}) {
  const { context, memberId } = outfit;
  const [resource, setResource] = useState<{
    loading: boolean;
    detail?: MemberDetail;
    error?: string;
  }>({ loading: true });

  useEffect(() => {
    let current = true;
    if (!memberId) {
      setResource({ loading: false, error: 'This outfit does not say which member it is for.' });
      return undefined;
    }
    setResource({ loading: true });
    memberReader(memberId)
      .then((detail) => {
        if (current) setResource({ loading: false, detail });
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setResource({
          loading: false,
          error:
            cause instanceof Error ? cause.message : "This member's details could not be loaded.",
        });
      });
    return () => {
      current = false;
    };
  }, [memberId, memberReader]);

  const assigned = assignedLabel(outfit);
  const detail = resource.detail;
  const email = context.email ?? (detail ? memberEmail(detail) : undefined);
  const updated = detail ? profileUpdated(detail) : undefined;
  const wardrobeTotal = detail ? Math.max(detail.garments_total ?? 0, detail.garments.length) : 0;

  return (
    <section aria-label="Member context" className="flex flex-col gap-4">
      <Card>
        <div className="flex items-center gap-4">
          <MemberAvatar name={context.displayName} src={context.avatarUrl} size="lg" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className="truncate text-lg font-semibold text-primary">{context.displayName}</h2>
            {email ? <span className="truncate text-sm text-tertiary">{email}</span> : null}
            {assigned ? <span className="text-xs text-tertiary">{assigned}</span> : null}
          </div>
        </div>
        <DetailList
          dense
          items={[
            ['Ships for', `${weekdayFor(outfit.forDate)} · ${outfit.forDate}`],
            ['Location', outfit.location ?? 'Not recorded'],
            ['Review', <StatusBadge value={outfit.reviewStatus} />],
            ['Try-on picture', renderLabel(outfit)],
            ['Generated', relativeTime(outfit.createdAt)],
            [
              'Wardrobe',
              detail ? `${wardrobeTotal} garment${wardrobeTotal === 1 ? '' : 's'}` : '…',
            ],
          ]}
        />
        {memberId ? (
          <div>
            <Button
              size="sm"
              color="link-color"
              href={`/clients/${encodeURIComponent(memberId)}?tab=profile`}
            >
              Open member page
            </Button>
          </div>
        ) : null}
      </Card>
      {resource.loading ? (
        <p className="text-sm text-tertiary">Loading this member's profile…</p>
      ) : resource.error ? (
        <Callout tone="neutral" title="Profile not shown">
          <FailureText raw={resource.error} />
        </Callout>
      ) : detail ? (
        <>
          {updated ? <p className="text-xs text-tertiary">{updated}</p> : null}
          {profileGroups(detail).map((group) => (
            <ProfileGroup key={group.title} dense {...group} />
          ))}
        </>
      ) : null}
    </section>
  );
}

export function OutfitReviewPanel({
  outfit,
  service = reviewService,
  wardrobeReader = readSlotWardrobe,
  memberReader = readMember,
  onSubmitted,
  nextHref,
}: {
  outfit: QueuedOutfit;
  service?: ReviewService;
  wardrobeReader?: WardrobeReader;
  memberReader?: MemberReader;
  onSubmitted?: () => void;
  /** The next outfit in the queue, offered once this one is decided. */
  nextHref?: string;
}) {
  const [finalIds, setFinalIds] = useState<string[]>(
    outfit.garments.map((garment) => garment.garmentId),
  );
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [receipt, setReceipt] = useState<DecisionReceipt>();
  const [pendingCover, setPendingCover] = useState<ReviewSubmission>();
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejection, setRejection] = useState<RejectReceipt>();
  const coverName = coveringFor(outfit);
  const lookName = outfit.lookIndex ? `Look ${outfit.lookIndex}` : 'this look';
  const [wardrobeQuery, setWardrobeQuery] = useState('');
  const [wardrobeOffset, setWardrobeOffset] = useState(0);
  const [wardrobeResource, setWardrobeResource] = useState<{
    loading: boolean;
    page?: WardrobePage;
    unavailable?: boolean;
    error?: string;
  }>({ loading: true });
  const [knownWardrobeGarments, setKnownWardrobeGarments] = useState<ReviewGarment[]>([]);

  useEffect(() => {
    let current = true;
    setWardrobeResource({ loading: true });
    void wardrobeReader(outfit.slotId, wardrobeQuery, WARDROBE_PAGE_SIZE, wardrobeOffset)
      .then((page) => {
        if (!current) return;
        if (!page) {
          setWardrobeResource({ loading: false, unavailable: true });
          return;
        }
        setWardrobeResource({ loading: false, page });
        setKnownWardrobeGarments((known) => {
          const latest = new Map(known.map((garment) => [garment.garmentId, garment]));
          page.items.forEach((garment) => latest.set(garment.garmentId, garment));
          return [...latest.values()];
        });
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setWardrobeResource({
          loading: false,
          error: cause instanceof Error ? cause.message : 'The wardrobe could not be loaded.',
        });
      });
    return () => {
      current = false;
    };
  }, [outfit.slotId, wardrobeOffset, wardrobeQuery, wardrobeReader]);

  // Keep only ids in edit state so refreshed queue and wardrobe payloads can
  // replace expired signed image URLs without discarding the selection.
  const final = useMemo(() => {
    const byId = new Map(
      [...outfit.garments, ...knownWardrobeGarments].map((garment) => [garment.garmentId, garment]),
    );
    return finalIds.flatMap((garmentId) => {
      const garment = byId.get(garmentId);
      return garment ? [garment] : [];
    });
  }, [finalIds, knownWardrobeGarments, outfit.garments]);
  const nameOf = useMemo(() => {
    const titles = new Map(outfit.garments.map((garment) => [garment.garmentId, garment.title]));
    return (garmentId: string) => titles.get(garmentId) ?? garmentId;
  }, [outfit]);
  // Presentation only: the server does its own diff and derives the decision.
  const diff = diffCombination(outfit.originalGarmentIds, finalIds);
  const editing = diff.changed;
  const chosen = new Set(finalIds);

  const remove = (garmentId: string) => {
    setFinalIds((current) => current.filter((id) => id !== garmentId));
  };

  const place = (garment: ReviewGarment) => {
    setFinalIds((current) => {
      if (current.includes(garment.garmentId)) return current;
      return [...current, garment.garmentId];
    });
  };

  const send = (submission: ReviewSubmission) => {
    setError(undefined);
    setBusy(true);
    // Never retry this automatically. A repeated decision is rejected with a
    // conflict, so a failure surfaces here and waits for an explicit refresh.
    void service
      .submitDecision(submission)
      .then((recorded) => {
        setReceipt(recorded);
        setDone(true);
        onSubmitted?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'This review could not be recorded.');
      })
      .finally(() => setBusy(false));
  };

  const reject = () => {
    setConfirmingReject(false);
    setError(undefined);
    setBusy(true);
    // A repeated reject returns the same replacement (`idempotent`), so it is
    // recorded the same way; the button stays disabled after the first one.
    void service
      .rejectLook({ slotId: outfit.slotId, comment: rejectComment })
      .then((recorded) => {
        setRejection(recorded ?? { replacementSlotId: null, idempotent: false });
        setDone(true);
        onSubmitted?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'This look could not be rejected.');
      })
      .finally(() => setBusy(false));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submission: ReviewSubmission = {
      slotId: outfit.slotId,
      finalGarmentIds: finalIds,
      comment: editing ? comment.trim() : '',
    };
    const blocked = submissionBlockedReason(submission, outfit.originalGarmentIds);
    if (blocked) {
      setError(blocked);
      return;
    }
    if (coverName) {
      setPendingCover(submission);
      return;
    }
    send(submission);
  };

  const commentMissing = editing && !comment.trim();

  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <WhyThisCombination outfit={outfit} />
        <Section
          title="The combination"
          description="Garments only. The avatar render happens after approval."
          actions={
            <StatusBadge
              value={rejection ? 'rejected' : editing ? 'edited' : outfit.reviewStatus}
            />
          }
        >
          {final.length === 0 ? (
            <Callout tone="warning" title="Every garment has been removed">
              Add at least one garment before submitting this edit.
            </Callout>
          ) : (
            <ul aria-label="Chosen combination" className={GARMENT_GRID}>
              {final.map((garment) => (
                <GarmentCard
                  key={garment.garmentId}
                  garment={garment}
                  actions={
                    <Button
                      size="sm"
                      color="secondary-destructive"
                      onClick={() => remove(garment.garmentId)}
                    >
                      Remove
                    </Button>
                  }
                />
              ))}
            </ul>
          )}
        </Section>

        <FormCard
          title={editing ? 'Submit your edit' : 'Approve as generated'}
          description={
            editing
              ? 'You changed the garments. Say what you changed and why, then submit.'
              : 'Looks right? Approve it as it is. To change it, remove garments above or add some from the wardrobe below - you will then be asked why.'
          }
          onSubmit={submit}
        >
          <TextAreaField
            label={
              editing
                ? 'What changed and why (required)'
                : 'What changed and why (only needed for an edit)'
            }
            name="comment"
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          {error ? (
            <Callout tone="error" title="This review was not recorded">
              <FailureText raw={error} />
            </Callout>
          ) : null}
          {rejection ? (
            <Callout
              tone="warning"
              title={`${outfit.lookIndex ? `Look ${outfit.lookIndex}` : 'Look'} rejected`}
              actions={
                nextHref ? (
                  <Button size="sm" href={nextHref}>
                    Review the next outfit
                  </Button>
                ) : undefined
              }
            >
              <span className="block">Generating replacement…</span>
              <span className="block">
                It comes back to tomorrow's outfits for review once it is ready.
              </span>
              {rejection.coveredForStylistId ? (
                <span className="mt-1 block font-medium">
                  Covered for {coverName ?? 'the assigned stylist'}
                </span>
              ) : null}
            </Callout>
          ) : done ? (
            <Callout
              tone="success"
              title="Review recorded"
              actions={
                nextHref ? (
                  <Button size="sm" href={nextHref}>
                    Review the next outfit
                  </Button>
                ) : undefined
              }
            >
              {receipt ? (
                <ServerVerdict receipt={receipt} nameOf={nameOf} />
              ) : (
                'This day-ahead decision was recorded.'
              )}
              {receipt?.coveredForStylistId ? (
                <span className="mt-1 block font-medium">
                  Covered for {coverName ?? 'the assigned stylist'}
                </span>
              ) : null}
            </Callout>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" size="sm" isLoading={busy} isDisabled={commentMissing || done}>
              {editing ? 'Submit edit' : 'Approve as generated'}
            </Button>
            {diff.changed ? (
              <Button
                size="sm"
                color="secondary"
                onClick={() => setFinalIds(outfit.garments.map((garment) => garment.garmentId))}
              >
                Reset to the model's combination
              </Button>
            ) : (
              <Button
                size="sm"
                color="secondary-destructive"
                isDisabled={busy || done}
                onClick={() => setConfirmingReject(true)}
              >
                Reject look
              </Button>
            )}
            {commentMissing ? (
              <span className="text-sm text-tertiary">
                A comment is required before an edit can be submitted.
              </span>
            ) : null}
          </div>
        </FormCard>

        <div role="separator" className="border-t border-secondary" />

        <Section title="Wardrobe" description="Browse and add garments eligible for this outfit.">
          <div className="flex flex-col gap-4">
            <TextField
              label="Search wardrobe"
              name="wardrobe-query"
              type="search"
              value={wardrobeQuery}
              onChange={(event) => {
                setWardrobeQuery(event.target.value);
                setWardrobeOffset(0);
              }}
              placeholder="Search by garment details"
            />
            {wardrobeResource.loading ? (
              <Callout tone="info" title="Loading wardrobe">
                Loading garments from this member's wardrobe.
              </Callout>
            ) : wardrobeResource.unavailable ? (
              <Callout tone="warning" title="Wardrobe unavailable">
                This member's wardrobe list cannot be loaded yet.
              </Callout>
            ) : wardrobeResource.error ? (
              <Callout tone="error" title="The wardrobe could not be loaded">
                <FailureText raw={wardrobeResource.error} />
              </Callout>
            ) : wardrobeResource.page?.items.length === 0 ? (
              <Callout
                tone="info"
                title={wardrobeQuery ? 'No garments found' : 'This member has no garments'}
              >
                {wardrobeQuery
                  ? 'No wardrobe garments match this search.'
                  : "There are no garments in this member's wardrobe."}
              </Callout>
            ) : wardrobeResource.page ? (
              <>
                <ul aria-label="Member wardrobe" className={GARMENT_GRID}>
                  {wardrobeResource.page.items.map((garment) => (
                    <GarmentCard
                      key={garment.garmentId}
                      garment={garment}
                      actions={
                        <Button
                          size="sm"
                          color="secondary"
                          isDisabled={chosen.has(garment.garmentId)}
                          onClick={() => place(garment)}
                        >
                          {chosen.has(garment.garmentId) ? 'In combination' : 'Add'}
                        </Button>
                      }
                    />
                  ))}
                </ul>
                {wardrobeResource.page.total > wardrobeResource.page.limit ? (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-tertiary">
                      {wardrobeResource.page.offset + 1}–
                      {Math.min(
                        wardrobeResource.page.offset + wardrobeResource.page.items.length,
                        wardrobeResource.page.total,
                      )}{' '}
                      of {wardrobeResource.page.total}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        color="secondary"
                        isDisabled={wardrobeResource.page.offset === 0}
                        onClick={() =>
                          setWardrobeOffset(
                            Math.max(
                              0,
                              wardrobeResource.page!.offset - wardrobeResource.page!.limit,
                            ),
                          )
                        }
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        color="secondary"
                        isDisabled={
                          wardrobeResource.page.offset + wardrobeResource.page.items.length >=
                          wardrobeResource.page.total
                        }
                        onClick={() =>
                          setWardrobeOffset(
                            wardrobeResource.page!.offset + wardrobeResource.page!.limit,
                          )
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </Section>
      </div>

      {/* Pinned below the workspace header for the whole review: the page scrolls
          the combination and wardrobe on the left while the member stays put. */}
      <aside
        aria-label="Review sidebar"
        className="flex w-full flex-col gap-5 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6.5rem)] lg:w-[26rem] lg:shrink-0 lg:overflow-y-auto xl:w-[30rem]"
      >
        <MemberBrief outfit={outfit} memberReader={memberReader} />
      </aside>
      {pendingCover && coverName ? (
        <ActionDialog
          title={`You're covering for ${coverName}`}
          description={`${outfit.context.displayName}${outfit.context.email ? ` (${outfit.context.email})` : ''} is ${coverName}'s member, not yours. Your decision is recorded as covering for them.`}
          onClose={() => setPendingCover(undefined)}
          footer={
            <>
              <Button size="sm" color="secondary" onClick={() => setPendingCover(undefined)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const submission = pendingCover;
                  setPendingCover(undefined);
                  send(submission);
                }}
              >
                {editing ? 'Submit edit' : 'Approve as generated'}
              </Button>
            </>
          }
        >
          <p className="text-sm text-secondary">
            {coverName} reviews this member first. Go ahead if you are helping out.
          </p>
        </ActionDialog>
      ) : null}
      {confirmingReject ? (
        <ActionDialog
          title={`Reject ${lookName}?`}
          description={`A replacement for ${lookName} is generated and comes back to tomorrow's outfits for review.`}
          onClose={() => setConfirmingReject(false)}
          footer={
            <>
              <Button size="sm" color="secondary" onClick={() => setConfirmingReject(false)}>
                Cancel
              </Button>
              <Button size="sm" color="primary-destructive" onClick={reject}>
                Reject look
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            {coverName ? (
              <p className="text-sm text-secondary">
                {outfit.context.displayName} is {coverName}'s member. Your rejection is recorded as
                covering for them.
              </p>
            ) : null}
            <TextAreaField
              label="Why (optional)"
              name="reject-comment"
              rows={3}
              value={rejectComment}
              onChange={(event) => setRejectComment(event.target.value)}
            />
          </div>
        </ActionDialog>
      ) : null}
    </div>
  );
}

/** Reads one slot by id, whatever its review status. */
function useSlot(service: ReviewService, slotId: string) {
  const [resource, setResource] = useState<{
    loading: boolean;
    outfit?: QueuedOutfit | undefined;
    error?: string;
  }>({ loading: true });
  const load = useCallback(async () => {
    setResource((current) => ({ ...current, loading: true, error: undefined }));
    try {
      setResource({ loading: false, outfit: await service.loadSlot(slotId) });
    } catch (cause) {
      setResource({
        loading: false,
        error: cause instanceof Error ? cause.message : 'This outfit could not be loaded.',
      });
    }
  }, [service, slotId]);
  useEffect(() => void load(), [load]);
  return { resource, load };
}

const SETTLED: Record<
  Exclude<QueuedOutfit['reviewStatus'], 'awaiting_review'>,
  { title: string; message: string; tone: 'success' | 'neutral' | 'info' }
> = {
  approved: {
    title: 'Already reviewed',
    message: 'A stylist has already approved or edited this outfit. This is the final combination.',
    tone: 'success',
  },
  shipped_unactioned: {
    title: 'Shipped as generated',
    message: 'Nobody reviewed this outfit by the 11 PM IST deadline, so it shipped as generated.',
    tone: 'neutral',
  },
  awaiting_generation: {
    title: 'Still being generated',
    message: 'This outfit is not ready to review yet. Refresh in a few minutes.',
    tone: 'info',
  },
  rejected: {
    title: 'Look rejected',
    message: 'A stylist rejected this look. Its replacement comes back to the queue for review.',
    tone: 'neutral',
  },
  unfilled: {
    title: "Couldn't create this look",
    message: 'The generator could not put this look together, so there is nothing to review.',
    tone: 'neutral',
  },
};

/**
 * What a settled slot's notice says: who approved it and when, rather than an anonymous
 * "a stylist", and - for a day that has passed - that the look can only be viewed, so a
 * stylist opening an old look does not read it as being locked out of their member.
 */
export function settledMessage(outfit: QueuedOutfit, now: Date = new Date()): string {
  if (outfit.reviewStatus === 'awaiting_review') return '';
  let message = SETTLED[outfit.reviewStatus].message;
  if (outfit.reviewStatus === 'approved') {
    const reviewer = reviewerLabel(outfit);
    if (reviewer) {
      const when = outfit.reviewedAt ? ` on ${shortDate(outfit.reviewedAt)}` : '';
      message = `${reviewer} approved this look${when}. This is the final combination.`;
    }
  }
  if (outfit.forDate < istDay(now)) {
    message += ` It was the look for ${shortDate(outfit.forDate)}, a day that has passed, so it can only be viewed.`;
  }
  return message;
}

/** A slot that is not awaiting review: what happened to it, and its combination. */
function SettledSlot({ outfit }: { outfit: QueuedOutfit }) {
  if (outfit.reviewStatus === 'awaiting_review') return null;
  const settled = SETTLED[outfit.reviewStatus];
  return (
    <div className="flex flex-col gap-4">
      <Callout tone={settled.tone} title={settled.title}>
        {settledMessage(outfit)}
      </Callout>
      <ReviewRecord outfit={outfit} />
      <WhyThisCombination outfit={outfit} />
      {outfit.garments.length ? (
        <Section title="Combination">
          <GarmentThumbs
            garments={outfit.garments}
            label={`${outfit.context.displayName}'s combination`}
            max={12}
          />
        </Section>
      ) : null}
    </div>
  );
}

export function OutfitReviewScreen({ service = reviewService }: { service?: ReviewService }) {
  // The existing route-param name is retained in App.tsx, but its value is the
  // day-ahead slot id. The slot itself is read by id, so a deep link or refresh
  // works whatever page of the queue it sits on and whatever its status. The
  // queue offset and search travel with the link only for previous/next.
  const { outfitId: slotId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const parsedOffset = Number(searchParams.get('offset'));
  const offset = Number.isInteger(parsedOffset) && parsedOffset >= 0 ? parsedOffset : 0;
  const q = searchParams.get('q')?.trim() ?? '';
  // Present only when the queue page offered scoping, so the same page is read.
  const scope = parseScope(searchParams.get('scope'));
  const { resource: slot, load } = useSlot(service, slotId);
  const { resource: queueResource } = useReviewQueue(service, {
    offset,
    ...(q ? { q } : {}),
    ...(scope ? { scope } : {}),
  });
  const outfit = slot.outfit;
  const outfits = queueResource.data?.outfits ?? [];
  const index = outfits.findIndex((item) => item.slotId === slotId);
  // Slots decided in this visit stay in the loaded page; skip them when stepping.
  const [decided, setDecided] = useState<ReadonlySet<string>>(() => new Set());
  const open = (item: QueuedOutfit) => !decided.has(item.slotId);
  const previous = index > 0 ? outfits.slice(0, index).reverse().find(open) : undefined;
  const next = index >= 0 ? outfits.slice(index + 1).find(open) : undefined;
  const queueHref = `/review?${new URLSearchParams({
    ...(offset ? { offset: String(offset) } : {}),
    ...(q ? { q } : {}),
    ...(scope && scope !== 'mine' ? { scope } : {}),
  }).toString()}`;
  const position =
    queueResource.data && index >= 0
      ? `${queueResource.data.offset + index + 1} of ${queueResource.data.total}`
      : undefined;
  const reviewable = outfit?.reviewStatus === 'awaiting_review';

  return (
    <Page
      title={
        outfit
          ? `${outfit.context.displayName}'s ${outfit.lookIndex ? `look ${outfit.lookIndex}` : 'outfit'}`
          : 'Outfit review'
      }
      description="Approve the combination as it is, or change it and say why."
      actions={
        index >= 0 ? (
          <>
            <Button
              size="sm"
              color="secondary"
              isDisabled={!previous}
              {...(previous ? { href: reviewHref(previous.slotId, offset, q, scope) } : {})}
            >
              Previous outfit
            </Button>
            <Button
              size="sm"
              color="secondary"
              isDisabled={!next}
              {...(next ? { href: reviewHref(next.slotId, offset, q, scope) } : {})}
            >
              Next outfit
            </Button>
          </>
        ) : undefined
      }
      meta={
        outfit ? (
          <>
            {outfit.forDate ? (
              <MetaItem
                label="Ships for"
                value={`${weekdayFor(outfit.forDate)} · ${outfit.forDate}`}
              />
            ) : null}
            {position ? <MetaItem label="In the queue" value={position} /> : null}
          </>
        ) : undefined
      }
    >
      <div>
        <Button size="sm" color="link-gray" iconLeading={ArrowLeft} href={queueHref}>
          Back to tomorrow's outfits
        </Button>
      </div>
      <ResourceState
        loading={slot.loading && !outfit}
        loadingLabel="this outfit"
        error={slot.error}
        empty={!outfit}
        emptyTitle="Outfit not found"
        emptyMessage="This outfit does not exist or is not visible to you. Go back to tomorrow's outfits."
        onRetry={() => void load()}
      >
        {outfit && reviewable ? (
          <OutfitReviewPanel
            key={outfit.slotId}
            outfit={outfit}
            service={service}
            onSubmitted={() => setDecided((current) => new Set([...current, outfit.slotId]))}
            {...(next ? { nextHref: reviewHref(next.slotId, offset, q, scope) } : {})}
          />
        ) : outfit ? (
          <SettledSlot outfit={outfit} />
        ) : null}
      </ResourceState>
    </Page>
  );
}
